/**
 * ============================================================================
 * SystemLog 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义系统日志（登录日志）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createSystemLogApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createSystemLogApi } from '@bayes-mes/shared/api/system-log/factory'
 * export const systemLogApi = createSystemLogApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await systemLogApi.getLoginLogs({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createSystemLogApi } from '@bayes-mes/shared/api/system-log/factory'
 * export const systemLogApi = createSystemLogApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/system-log/factory
 * @description 系统日志（登录日志）模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有登录日志查询和统计方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  LoginAttempt,                  // 登录尝试记录
  LoginLogQueryParams,           // 登录日志列表查询参数
  LoginStatsQueryParams,         // 登录统计查询参数
  LoginTrendQueryParams,         // 登录趋势查询参数
  SuspiciousLogsQueryParams,     // 异常登录查询参数
  ExportLoginLogsParams,         // 导出参数
  LoginStatistics,               // 登录统计数据
  LoginTrendPoint,               // 登录趋势数据点
  SuspiciousLogins,              // 异常登录数据
  LoginLogExportResult,          // 导出结果
} from '../../types/system-log'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建系统日志模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有系统日志（登录日志）相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有系统日志相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createSystemLogApi(client: IRequestClient) {
  return {
    // ===== 登录日志查询 =====
    
    /**
     * 获取登录日志列表（分页）
     * 
     * 使用场景：
     * - 在系统日志管理页面的登录日志列表中使用
     * - 支持分页、搜索、多条件筛选功能
     * - 管理员可以查看所有用户的登录历史
     * - 支持按用户名、IP、设备、时间范围、成功/失败状态筛选
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 20
     *     示例：per_page: 20（每页20条）
     *   - username?: string - 用户名（模糊搜索）
     *     示例：username: 'admin'（搜索包含"admin"的用户名）
     *   - ip_address?: string - IP地址（模糊搜索）
     *     示例：ip_address: '192.168'（搜索以"192.168"开头的IP）
     *   - is_successful?: boolean - 是否成功（true=成功，false=失败）
     *     示例：is_successful: false（只查询失败的登录）
     *   - device_name?: string - 设备名称（模糊搜索）
     *     示例：device_name: 'Chrome'（搜索使用Chrome浏览器的登录）
     *   - start_date?: string - 开始日期（YYYY-MM-DD 格式）
     *     示例：start_date: '2025-10-01'（从10月1日开始）
     *   - end_date?: string - 结束日期（YYYY-MM-DD 格式）
     *     示例：end_date: '2025-10-18'（到10月18日结束）
     * 
     * @returns Promise<PaginationResult<LoginAttempt>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     items: LoginAttempt[],   // 登录记录数组，每个元素是一个登录尝试
     *     total: number,           // 总记录数，如 158（共有158条记录）
     *     page: number,            // 当前页码，如 2（正在查看第2页）
     *     per_page: number,        // 每页数量，如 20（每页显示20条）
     *     totalPages: number       // 总页数，如 8（总共8页）
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：管理员在登录日志页面设置筛选条件并点击"查询"
     * 2. 前端调用：组件调用 systemLogApi.getLoginLogs({ page: 1, username: 'admin', is_successful: false })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/logs/login?page=1&username=admin&is_successful=false
     * 5. 后端处理：
     *    - 路由匹配到 LogController@loginLogs 方法
     *    - 应用筛选条件（username、is_successful 等）
     *    - 查询数据库，按尝试时间倒序排列
     *    - 应用分页（计算 offset 和 limit）
     *    - 通过 LoginAttemptResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<LoginAttempt> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await systemLogApi.getLoginLogs()
     * console.log(page1.items)  // 登录记录数组
     * console.log(page1.total)  // 总记录数
     * 
     * // 示例2：查询失败的登录记录
     * const failedLogins = await systemLogApi.getLoginLogs({
     *   is_successful: false,
     *   page: 1,
     *   per_page: 50
     * })
     * 
     * // 示例3：按时间范围查询
     * const logsInRange = await systemLogApi.getLoginLogs({
     *   start_date: '2025-10-01',
     *   end_date: '2025-10-18'
     * })
     * 
     * // 示例4：组合查询（用户名 + 时间范围 + 成功状态）
     * const result = await systemLogApi.getLoginLogs({
     *   username: 'admin',
     *   start_date: '2025-10-15',
     *   end_date: '2025-10-18',
     *   is_successful: true,
     *   page: 1,
     *   per_page: 20
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字、日期格式错误等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getLoginLogs(params?: LoginLogQueryParams) {
      // 调用请求客户端的 get 方法
      // - 第一个参数：URL 路径
      // - 第二个参数：配置对象，{ params } 会被转换为 URL 查询参数
      // - 泛型参数：指定返回数据的类型
      return client.get<PaginationResult<LoginAttempt>>(API_ROUTES.logs.list, { params })
    },
    
    // ===== 登录统计分析 =====
    
    /**
     * 获取登录统计数据
     * 
     * 使用场景：
     * - 在系统仪表板显示登录统计概览
     * - 在登录日志页面显示统计卡片
     * - 安全监控：查看登录成功率、失败次数等指标
     * - 用户行为分析：查看最活跃用户、最活跃IP等
     * 
     * @param params - 统计查询参数（可选）
     *   - period?: 'today' | 'week' | 'month' | 'year' - 统计周期
     *     默认：'today'（今天）
     *     示例：period: 'week'（统计本周数据）
     * 
     * @returns Promise<LoginStatistics> 登录统计数据对象
     *   返回的数据结构：
     *   {
     *     summary: {               // 汇总数据
     *       totalAttempts: 150,    // 总登录次数
     *       successfulAttempts: 143, // 成功登录次数
     *       failedAttempts: 7,     // 失败登录次数
     *       uniqueUsers: 25,       // 独立用户数
     *       uniqueIps: 18,         // 独立IP数
     *       successRate: 95.33     // 成功率（百分比）
     *     },
     *     topUsers: [              // 最活跃用户 TOP 10
     *       { username: 'admin', loginCount: 15 },
     *       { username: 'user01', loginCount: 12 },
     *       ...
     *     ],
     *     topIps: [                // 最活跃IP TOP 10
     *       { ipAddress: '192.168.1.100', accessCount: 20 },
     *       { ipAddress: '192.168.1.101', accessCount: 15 },
     *       ...
     *     ],
     *     failureReasons: [        // 失败原因统计
     *       { failureReason: '密码错误', count: 5 },
     *       { failureReason: '账号被锁定', count: 2 },
     *       ...
     *     ],
     *     deviceStats: [           // 设备类型统计 TOP 10
     *       { deviceName: 'Chrome 118', count: 50 },
     *       { deviceName: 'Firefox 119', count: 30 },
     *       ...
     *     ]
     *   }
     * 
     * 数据流向：
     * 1. 用户打开仪表板或切换统计周期（今天/本周/本月/本年）
     * 2. 前端调用：systemLogApi.getLoginStats({ period: 'week' })
     * 3. 发送请求：GET /api/v1/logs/login/stats?period=week
     * 4. 后端处理：LogController@loginStats 方法
     *    - 根据 period 确定时间范围
     *    - 统计总登录次数、成功/失败次数
     *    - 统计独立用户数、独立IP数
     *    - 计算成功率
     *    - 查询最活跃用户 TOP 10（按登录次数排序）
     *    - 查询最活跃IP TOP 10（按访问次数排序）
     *    - 统计失败原因分布
     *    - 统计设备类型分布 TOP 10
     *    - 格式化数据（snake_case → camelCase）
     * 5. 返回数据：完整的统计信息
     * 6. 前端显示：在仪表板上展示各种统计图表和卡片
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取今天的统计（默认）
     * const todayStats = await systemLogApi.getLoginStats()
     * console.log(`成功率：${todayStats.summary.successRate}%`)
     * 
     * // 示例2：获取本周的统计
     * const weekStats = await systemLogApi.getLoginStats({ period: 'week' })
     * 
     * // 示例3：显示在仪表板卡片中
     * const stats = await systemLogApi.getLoginStats({ period: 'today' })
     * // 显示总登录次数
     * totalCard.value = stats.summary.totalAttempts
     * // 显示成功率
     * successRateCard.value = `${stats.summary.successRate}%`
     * // 显示最活跃用户
     * topUsersChart.data = stats.topUsers.map(u => ({
     *   name: u.username,
     *   value: u.loginCount
     * }))
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（period 不是有效值）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getLoginStats(params?: LoginStatsQueryParams) {
      return client.get<LoginStatistics>(API_ROUTES.logs.stats, { params })
    },
    
    /**
     * 获取登录趋势数据（按小时/天统计）
     * 
     * 使用场景：
     * - 在趋势图表中显示登录活跃度变化
     * - 分析登录高峰时段
     * - 监控异常登录趋势
     * - 对比成功和失败登录的时间分布
     * 
     * @param params - 趋势查询参数（可选）
     *   - period?: 'today' | 'week' | 'month' | 'year' - 统计周期
     *     默认：'today'（今天）
     *     示例：period: 'week'（统计本周数据）
     *   - groupBy?: 'hour' | 'day' - 分组方式
     *     默认：'hour'（按小时分组）
     *     示例：groupBy: 'day'（按天分组）
     * 
     * @returns Promise<LoginTrendPoint[]> 登录趋势数据点数组
     *   返回的数据结构：
     *   [
     *     {
     *       timePeriod: '2025-10-18 14:00:00',  // 时间段
     *       successful: 25,                      // 成功登录次数
     *       failed: 2,                          // 失败登录次数
     *       total: 27                           // 总次数
     *     },
     *     {
     *       timePeriod: '2025-10-18 15:00:00',
     *       successful: 30,
     *       failed: 1,
     *       total: 31
     *     },
     *     ...
     *   ]
     * 
     * 数据流向：
     * 1. 用户打开登录趋势图表页面或切换时间范围/分组方式
     * 2. 前端调用：systemLogApi.getLoginTrend({ period: 'today', groupBy: 'hour' })
     * 3. 发送请求：GET /api/v1/logs/login/trend?period=today&groupBy=hour
     * 4. 后端处理：LogController@loginTrend 方法
     *    - 根据 period 确定时间范围
     *    - 根据 groupBy 确定分组格式（小时或天）
     *    - 按时间段分组统计成功和失败的登录次数
     *    - 计算每个时间段的总次数
     *    - 按时间顺序排列
     *    - 格式化数据（snake_case → camelCase）
     * 5. 返回数据：时间序列数据数组
     * 6. 前端显示：在折线图或柱状图中展示趋势
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取今天按小时的趋势
     * const hourlyTrend = await systemLogApi.getLoginTrend({
     *   period: 'today',
     *   groupBy: 'hour'
     * })
     * 
     * // 示例2：获取本月按天的趋势
     * const dailyTrend = await systemLogApi.getLoginTrend({
     *   period: 'month',
     *   groupBy: 'day'
     * })
     * 
     * // 示例3：在图表中显示
     * const trend = await systemLogApi.getLoginTrend({ period: 'week' })
     * const chartData = {
     *   xAxis: trend.map(t => t.timePeriod),
     *   series: [
     *     {
     *       name: '成功',
     *       data: trend.map(t => t.successful),
     *       color: '#67C23A'  // 绿色
     *     },
     *     {
     *       name: '失败',
     *       data: trend.map(t => t.failed),
     *       color: '#F56C6C'  // 红色
     *     }
     *   ]
     * }
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（period 或 groupBy 不是有效值）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getLoginTrend(params?: LoginTrendQueryParams) {
      return client.get<LoginTrendPoint[]>(API_ROUTES.logs.trend, { params })
    },
    
    /**
     * 获取异常登录行为数据
     * 
     * 使用场景：
     * - 安全监控：检测可疑的登录行为
     * - 识别暴力破解攻击（多次失败后成功）
     * - 识别共享账号（单个IP多账号登录）
     * - 识别恶意登录尝试（高频失败登录）
     * - 在安全告警页面显示异常登录列表
     * 
     * @param params - 查询参数（可选）
     *   - hours?: number - 查询最近多少小时的数据
     *     默认：24（最近24小时）
     *     示例：hours: 48（最近48小时）
     *     范围：1-168（最多7天）
     * 
     * @returns Promise<SuspiciousLogins> 异常登录行为数据对象
     *   返回的数据结构：
     *   {
     *     suspiciousUsers: [       // 异常用户（多次失败后成功的账号）
     *       {
     *         username: 'user01',
     *         ipAddress: '192.168.1.100',
     *         lastAttempt: '2025-10-18T15:30:00Z'
     *       },
     *       ...
     *     ],
     *     multiUserIps: [          // 多用户IP（单个IP多账号登录）
     *       {
     *         ipAddress: '192.168.1.100',
     *         userCount: 5         // 该IP登录了5个不同账号
     *       },
     *       ...
     *     ],
     *     highFailureIps: [        // 高频失败IP（高频失败登录）
     *       {
     *         ipAddress: '192.168.1.200',
     *         username: 'admin',
     *         failureCount: 10     // 尝试登录admin账号失败了10次
     *       },
     *       ...
     *     ]
     *   }
     * 
     * 数据流向：
     * 1. 用户打开安全监控页面或异常登录告警页面
     * 2. 前端调用：systemLogApi.getSuspiciousLogs({ hours: 24 })
     * 3. 发送请求：GET /api/v1/logs/login/suspicious?hours=24
     * 4. 后端处理：LogController@suspiciousLogs 方法
     *    - 查询最近 N 小时的登录记录
     *    - 检测多次失败后成功的账号（失败>=3次 且 成功>=1次）
     *    - 检测单个IP多账号登录（同一IP登录>=3个不同账号）
     *    - 检测高频失败登录（同一IP+账号组合失败>=5次）
     *    - 按相关性排序，限制返回数量（各取TOP 20）
     *    - 格式化数据（snake_case → camelCase）
     * 5. 返回数据：异常登录行为列表
     * 6. 前端显示：在告警列表中高亮显示，提供安全建议
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取最近24小时的异常登录
     * const suspicious = await systemLogApi.getSuspiciousLogs()
     * 
     * // 示例2：获取最近48小时的异常登录
     * const suspicious48h = await systemLogApi.getSuspiciousLogs({ hours: 48 })
     * 
     * // 示例3：显示异常登录告警
     * const suspicious = await systemLogApi.getSuspiciousLogs({ hours: 24 })
     * 
     * // 显示可疑用户告警
     * if (suspicious.suspiciousUsers.length > 0) {
     *   showAlert('检测到可疑登录行为', {
     *     type: 'warning',
     *     users: suspicious.suspiciousUsers.map(u => 
     *       `${u.username} (${u.ipAddress}) - ${u.lastAttempt}`
     *     )
     *   })
     * }
     * 
     * // 显示多用户IP告警
     * if (suspicious.multiUserIps.length > 0) {
     *   const highRiskIps = suspicious.multiUserIps.filter(ip => ip.userCount >= 5)
     *   if (highRiskIps.length > 0) {
     *     showAlert('检测到共享IP登录多账号', {
     *       type: 'danger',
     *       ips: highRiskIps
     *     })
     *   }
     * }
     * 
     * // 显示暴力破解告警
     * if (suspicious.highFailureIps.length > 0) {
     *   const attackAttempts = suspicious.highFailureIps.filter(
     *     item => item.failureCount >= 10
     *   )
     *   if (attackAttempts.length > 0) {
     *     showAlert('检测到暴力破解攻击', {
     *       type: 'danger',
     *       attempts: attackAttempts
     *     })
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（hours 超出范围）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getSuspiciousLogs(params?: SuspiciousLogsQueryParams) {
      return client.get<SuspiciousLogins>(API_ROUTES.logs.suspicious, { params })
    },
    
    // ===== 数据导出 =====
    
    /**
     * 导出登录日志数据
     * 
     * 使用场景：
     * - 导出登录日志用于离线分析
     * - 导出审计报告
     * - 归档历史登录记录
     * - 提供给安全团队进行安全审查
     * 
     * @param params - 导出参数（可选，复用列表查询参数）
     *   参数说明同 getLoginLogs()，用于筛选要导出的数据
     *   注意：后端限制最多导出 10000 条记录
     * 
     * @returns Promise<LoginLogExportResult> 导出结果对象
     *   返回的数据结构：
     *   {
     *     data: LoginAttempt[],  // 导出的登录记录数组
     *     count: number          // 导出的记录数量
     *   }
     * 
     * 数据流向：
     * 1. 用户在登录日志页面点击"导出"按钮
     * 2. 前端调用：systemLogApi.exportLoginLogs({ start_date: '2025-10-01', end_date: '2025-10-18' })
     * 3. 发送请求：POST /api/v1/logs/login/export
     *    请求体：{ start_date: '2025-10-01', end_date: '2025-10-18' }
     * 4. 后端处理：LogController@export 方法
     *    - 应用筛选条件（与 loginLogs 相同）
     *    - 查询符合条件的记录（限制最多10000条）
     *    - 按尝试时间倒序排列
     *    - 通过 LoginAttemptResource 格式化数据
     * 5. 返回数据：包含数据数组和数量的对象
     * 6. 前端处理：将数据转换为CSV/Excel格式并下载
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：导出所有登录记录
     * const exportResult = await systemLogApi.exportLoginLogs()
     * 
     * // 示例2：导出指定时间范围的记录
     * const exportResult = await systemLogApi.exportLoginLogs({
     *   start_date: '2025-10-01',
     *   end_date: '2025-10-18'
     * })
     * 
     * // 示例3：导出失败的登录记录
     * const failedExport = await systemLogApi.exportLoginLogs({
     *   is_successful: false,
     *   start_date: '2025-10-01'
     * })
     * 
     * // 示例4：导出并下载为CSV文件
     * const handleExport = async () => {
     *   const result = await systemLogApi.exportLoginLogs({
     *     start_date: '2025-10-01',
     *     end_date: '2025-10-18'
     *   })
     *   
     *   // 转换为CSV
     *   const csv = convertToCSV(result.data, [
     *     { key: 'username', label: '用户名' },
     *     { key: 'ipAddress', label: 'IP地址' },
     *     { key: 'isSuccessful', label: '是否成功' },
     *     { key: 'attemptedAt', label: '尝试时间' }
     *   ])
     *   
     *   // 下载文件
     *   downloadFile(csv, `login_logs_${Date.now()}.csv`, 'text/csv')
     * }
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败
     * - 500：服务器内部错误（数据库查询失败等）
     * - 413：数据量过大（超过10000条限制）
     */
    exportLoginLogs(params?: ExportLoginLogsParams) {
      return client.post<LoginLogExportResult>(API_ROUTES.logs.export, params)
    },
  }
}

